import React from 'react';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';
import styles from './Button.module.css';

const SPINNER_ICON_SIZE: Record<string, number> = { xs: 12, sm: 14, lg: 20 };

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  loadingText?: string;
  dataTestId?: string;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  loading = false,
  loadingText = 'Loading...',
  className = '',
  dataTestId = 'button',
  children,
  ...props
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case 'primary':
        return styles.buttonPrimary;
      case 'secondary':
        return styles.buttonSecondary;
      case 'danger':
        return styles.buttonDanger;
      case 'ghost':
        return styles.buttonGhost;
      default:
        return styles.buttonPrimary;
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'xs':
        return styles.buttonXs;
      case 'sm':
        return styles.buttonSm;
      case 'md':
        return styles.buttonMd;
      case 'lg':
        return styles.buttonLg;
      default:
        return styles.buttonMd;
    }
  };

  const buttonClass = clsx(styles.button, getVariantClass(), getSizeClass(), className);

  return (
    <button type={type} className={buttonClass} disabled={disabled || loading} data-test-id={dataTestId} {...props}>
      {loading ? (
        <span className={styles.loadingSpinner}>
          <Loader2
            className={styles.animateSpin}
            size={SPINNER_ICON_SIZE[size] ?? 16}
          />
          <span>{loadingText}</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
